import { Typography } from "@mui/material";
import { useNavigate } from "react-router";
import * as Style from "./ProductCard.styles.ts";
import Chip from "@mui/material/Chip";
import { useEffect, useRef, useState } from "react";
import { models } from "types/api/viewModels.ts";
import { MarketingMaterialsCart } from "features/MarketingMaterials/hooks/useMarketingMaterialsCart.ts";
import { getMerchandiseInventoryStatus } from "features/MarketingMaterials/utils/getMerchandiseInventoryStatus.ts";
import { getMerchandisePrimaryImageUrl } from "features/MarketingMaterials/utils/getMerchandiseImageUrls.ts";

interface ProductCardProps {
	product: models["MerchandiseProductViewModel"];
	cartData?: MarketingMaterialsCart;
	addCartItem: (item: models["MerchandiseProductViewModel"], quantity: number) => void;
}

export const ProductCard = ({ product, cartData, addCartItem }: ProductCardProps) => {
	const inventoryStatus = getMerchandiseInventoryStatus(product);
	const isBackordered = inventoryStatus === "backordered";
	const isOutOfStock = inventoryStatus === "out-of-stock";
	const isLimitedStock = inventoryStatus === "limited-stock";
	const primaryImageUrl = getMerchandisePrimaryImageUrl(product.images);

	const clickFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingQtyRef = useRef(0);

	const navigate = useNavigate();

	const queueAddToCart = () => {
		if (productInCart >= 999) return;

		pendingQtyRef.current += 1;

		if (productInCart > 0) {
			setProductInCart(productInCart + 1);
		} else {
			setProductInCart(pendingQtyRef.current);
		}

		// Reset trailing timer so we only send once after user stops clicking
		if (clickFlushTimerRef.current) {
			clearTimeout(clickFlushTimerRef.current);
		}

		clickFlushTimerRef.current = setTimeout(() => {
			const qtyToAdd = pendingQtyRef.current;
			pendingQtyRef.current = 0;

			if (qtyToAdd > 0) {
				addCartItem(product, qtyToAdd);
			}
		}, 350);
	};

	useEffect(() => {
		return () => {
			if (clickFlushTimerRef.current) {
				clearTimeout(clickFlushTimerRef.current);
			}
		};
	}, []);

	const [productInCart, setProductInCart] = useState(
		cartData?.cartItems?.find((item) => item.id === product.id)?.quantityOrdered ?? 0
	);

	const handleCardClick = () => {
		navigate(`items/${product.id}`);
	};

	useEffect(() => {
		const quantity = cartData?.cartItems?.find((item) => item.id === product.id)?.quantityOrdered ?? 0;
		setProductInCart(quantity);
	}, [cartData, product]);

	return (
		<Style.ProductCard>
			<Style.ProductCardActionArea onClick={handleCardClick}>
				<Style.ProductChipStack>
					{isBackordered && (
						<Style.BackorderedChip
							label="Backordered"
							variant="outlined"
						/>
					)}
					{isOutOfStock && (
						<Chip
							label="Out of Stock"
							variant="outlined"
						/>
					)}
					{isLimitedStock && (
						<Style.LimitedStockChip
							label="Limited Stock"
							variant="outlined"
						/>
					)}
				</Style.ProductChipStack>
				<Style.ProductCardMedia
					src={primaryImageUrl}
					alt={product.name ?? "Product image unavailable"}
				/>

				<Style.ProductCardContent>
					<Typography
						gutterBottom
						variant="h5"
						component="div"
					>
						{product.name}
					</Typography>

					<Style.ProductCardSKUTypography variant="body2">{product.sku}</Style.ProductCardSKUTypography>
				</Style.ProductCardContent>
			</Style.ProductCardActionArea>
			<Style.ProductCardBox>
				<Style.ProductCardPriceTypography
					gutterBottom={false}
					variant="h5"
				>
					${product.listPrice?.toFixed(2) ?? "0.00"}
				</Style.ProductCardPriceTypography>
				{!isOutOfStock && (
					<Style.CartButton onClick={queueAddToCart}>
						{productInCart > 0 && <Style.CartChip label={productInCart} />}
						<Style.ShoppingCartButton fontSize="large" />
					</Style.CartButton>
				)}
			</Style.ProductCardBox>
		</Style.ProductCard>
	);
};
